import React, { useState, useEffect } from 'react'
import { Navbar, Nav, Container, Button, Badge, Modal, Alert, Spinner } from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../services/supabase'

export default function Layout({ children, user }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [userEmail, setUserEmail] = useState('')

  // ─── Modal génération lien coach ─────────────────────────────────────────
  const [showCoachModal, setShowCoachModal] = useState(false)
  const [coachLink, setCoachLink] = useState('')
  const [linkLoading, setLinkLoading] = useState(false)
  const [linkError, setLinkError] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)

  useEffect(() => {
    const getUserEmail = async () => {
      if (user) {
        const { data: { user: currentUser } } = await supabase.auth.getUser()
        setUserEmail(currentUser?.email || 'Staff')
      }
    }
    getUserEmail()
  }, [user])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  // ─── Générer le lien d'invitation coach (PERMANENT) ───────────────────────
  const generateCoachLink = async () => {
    setLinkLoading(true)
    setLinkError('')
    setCoachLink('')
    setLinkCopied(false)

    try {
      const token = crypto.randomUUID()

      const { data, error } = await supabase
        .from('invite_tokens')
        .insert([{
          token,
          expires_at: null,   // ✅ Pas de date d'expiration → lien permanent
          role: 'coach'       // ✅ Marqué comme lien coach
        }])
        .select()
        .single()

      if (error) throw new Error(error.message)

      const link = `${window.location.origin}/register-coach?invite=${data.token}`
      setCoachLink(link)
    } catch (err) {
      setLinkError(err.message || 'Erreur lors de la génération du lien.')
    } finally {
      setLinkLoading(false)
    }
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(coachLink)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 3000)
    } catch {
      prompt('Copiez ce lien :', coachLink)
    }
  }

  const openModal = () => {
    setShowCoachModal(true)
    setCoachLink('')
    setLinkError('')
    setLinkCopied(false)
  }

  return (
    <div className="tablet-optimized">
      {user && (
        <Navbar bg="primary" variant="dark" expand="lg" className="mb-4 shadow">
          <Container fluid>
            <Navbar.Brand href="/" className="d-flex align-items-center">
              <div>
                <div className="fw-bold">Street Work</div>
                <small className="opacity-75">Gestion des présences</small>
              </div>
            </Navbar.Brand>

            <Navbar.Toggle aria-controls="basic-navbar-nav" />

            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto">
                <Nav.Link
                  href="/"
                  className={`fw-medium ${location.pathname === '/' ? 'active' : ''}`}
                >
                  Tableau de Bord
                </Nav.Link>
                <Nav.Link
                  href="/courses"
                  className={`fw-medium ${location.pathname === '/courses' ? 'active' : ''}`}
                >
                  Cours
                </Nav.Link>
                <Nav.Link
                  href="/members"
                  className={`fw-medium ${location.pathname === '/members' ? 'active' : ''}`}
                >
                  Membres
                </Nav.Link>
                <Nav.Link
                  href="/reports"
                  className={`fw-medium ${location.pathname === '/reports' ? 'active' : ''}`}
                >
                  Rapports
                </Nav.Link>
                <Nav.Link
                  href="/coaches"
                  className={`fw-medium ${location.pathname === '/coaches' ? 'active' : ''}`}
                >
                  🏅 Coachs
                </Nav.Link>

                {/* ── Inviter un coach ── */}
                <Nav.Link
                  onClick={openModal}
                  className="fw-medium"
                  style={{ cursor: 'pointer' }}
                >
                  ➕ Inviter un Coach
                </Nav.Link>
              </Nav>

              <Nav className="align-items-center">
                <div className="navbar-text text-light me-3 d-none d-md-block">
                  <Badge bg="light" text="dark" className="me-2">Staff</Badge>
                  <span className="fw-medium">{userEmail}</span>
                </div>
                <Button variant="outline-light" onClick={handleLogout} size="sm">
                  Déconnexion
                </Button>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      )}

      <Container fluid="md">
        {children}
      </Container>

      {/* ── Modal génération lien coach ──────────────────────────────────────── */}
      <Modal show={showCoachModal} onHide={() => setShowCoachModal(false)} centered>
        <Modal.Header
          closeButton
          style={{ background: 'linear-gradient(135deg, #1d6fa4, #0d47a1)', color: '#fff' }}
        >
          <Modal.Title>🏅 Inviter un Coach</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {/* ✅ Texte mis à jour — plus de mention "7 jours" */}
          <p className="text-muted mb-4">
            Générez un lien d'invitation à envoyer au coach.
            Le lien sera <strong>permanent</strong> et utilisable plusieurs fois.
          </p>

          {linkError && <Alert variant="danger">{linkError}</Alert>}

          {/* ── Avant génération ── */}
          {!coachLink && !linkLoading && (
            <div className="text-center py-3">
              <div style={{ fontSize: '3rem' }} className="mb-3">🔗</div>
              <Button variant="primary" size="lg" onClick={generateCoachLink} className="px-5">
                Générer le lien
              </Button>
            </div>
          )}

          {/* ── Chargement ── */}
          {linkLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Génération en cours...</p>
            </div>
          )}

          {/* ── Lien généré ── */}
          {coachLink && (
            <>
              {/* ✅ Message mis à jour — lien permanent */}
              <Alert variant="success" className="mb-3">
                ✅ Lien généré avec succès ! Ce lien est <strong>permanent</strong> et réutilisable.
              </Alert>

              <div
                className="p-3 bg-light border rounded mb-3"
                style={{ wordBreak: 'break-all', fontSize: '0.85rem', fontFamily: 'monospace' }}
              >
                {coachLink}
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant={linkCopied ? 'success' : 'primary'}
                  onClick={copyLink}
                  className="flex-grow-1"
                >
                  {linkCopied ? '✅ Copié !' : '📋 Copier le lien'}
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    const subject = encodeURIComponent('Invitation Coach - Street Sport Luxembourg')
                    const body = encodeURIComponent(
                      `Bonjour,\n\nVous êtes invité(e) à vous inscrire comme coach sur Street Sport Luxembourg.\n\n` +
                      `Cliquez sur le lien ci-dessous pour compléter votre inscription :\n\n${coachLink}\n\n` +
                      `Ce lien est permanent et peut être utilisé immédiatement.\n\n` +
                      `Cordialement,\nL'équipe Street Sport Luxembourg`
                    )
                    window.location.href = `mailto:?subject=${subject}&body=${body}`
                  }}
                >
                  ✉️ Envoyer par email
                </Button>

                <Button variant="outline-primary" onClick={generateCoachLink}>
                  🔄 Nouveau lien
                </Button>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCoachModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}